import { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so the API is only called once per page-load
let _cache = null;
let _inflight = null;
const _subs = new Set();

async function fetchOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/feature-flags/me`, { credentials: 'include' });
      const data = res.ok ? await res.json() : { disabled: [] };
      _cache = new Set(data.disabled || []);
      _subs.forEach((fn) => fn(_cache));
      return _cache;
    } catch (_e) {
      _cache = new Set();
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/**
 * useFeatureFlags — returns `{ disabled: Set<string>, isDisabled(key) }`.
 * Driven by GET /api/feature-flags/me. Super admins always get an empty set.
 */
export function useFeatureFlags() {
  const [disabled, setDisabled] = useState(_cache || new Set());

  useEffect(() => {
    let cancelled = false;
    const handler = (s) => { if (!cancelled) setDisabled(new Set(s)); };
    _subs.add(handler);
    fetchOnce().then((s) => { if (!cancelled) setDisabled(new Set(s)); });
    return () => { cancelled = true; _subs.delete(handler); };
  }, []);

  return {
    disabled,
    isDisabled: (key) => disabled.has(key),
  };
}

/** Force a refresh after SA updates flags (rare). */
export function invalidateFeatureFlags() {
  _cache = null;
  _inflight = null;
  fetchOnce();
}
